import { fourierSeries, keptPower, reconstruct } from "./fourier.js";

/**
 * Shared state of the page: the signals that can be analysed (the selected example and the user's drawing),
 * the one explained in the "mathematical process" section, and the number K of epicycles kept per coordinate.
 */
export const state = {
  sources: { example: null, drawing: null },
  active: "example",
  K: Infinity, //Infinity keeps every term.
};
const listeners = [];

/**
 * @function analyse Splits a path into the two signals x(t) and y(t) and computes their Fourier series.
 * @param {String} label Name of the path shown to the user.
 * @param {Array<{x: Number, y: Number}>} points Points of the path, in pixels from the centre of the drawing, y pointing up.
 */
export function analyse(label, points) {
  const x = points.map((p) => p.x);
  const y = points.map((p) => p.y);
  return { label, x, y, numPoints: points.length, fourierX: fourierSeries(x), fourierY: fourierSeries(y) };
}

/**
 * @function reconstructPath Path drawn by the K largest terms of each coordinate, sampled at numSamples times.
 */
export function reconstructPath(signal, K, numSamples) {
  const xs = reconstruct(signal.fourierX, K, numSamples);
  const ys = reconstruct(signal.fourierY, K, numSamples);
  return xs.map((x, i) => ({ x, y: ys[i] }));
}

export function maxTerms(signal) {
  return Math.max(signal.fourierX.terms.length, signal.fourierY.terms.length);
}

/**
 * @function summary What the K largest terms of each coordinate keep of a signal.
 * @return {{K: Number, max: Number, power: Number, rms: Number}} power is the kept fraction of the power (Parseval),
 * rms the root mean square distance in pixels between the sampled points and the reconstructed ones.
 */
export function summary(signal, K) {
  const { fourierX, fourierY } = signal;
  const max = maxTerms(signal);
  const total = fourierX.totalPower + fourierY.totalPower;
  const kept = keptPower(fourierX, K) + keptPower(fourierY, K);
  return {
    K: Math.min(K, max),
    max,
    power: total > 0 ? kept / total : 1,
    rms: Math.sqrt(Math.max(0, total - kept)),
  };
}

export function activeSignal() {
  return state.sources[state.active];
}

export function setSource(source, signal) {
  state.sources[source] = signal;
  state.active = source;
  notify();
}

export function selectSource(source) {
  if (state.sources[source] && state.active !== source) {
    state.active = source;
    notify();
  }
}

export function setK(K) {
  if (K !== state.K) {
    state.K = K;
    notify();
  }
}

export function onChange(listener) {
  listeners.push(listener);
}

function notify() {
  for (const listener of listeners) {
    listener(state);
  }
}

/**
 * @function kSteps Values offered by the epicycle sliders: every K up to 10, then about 20% more at each step, then every term.
 */
export function kSteps(max) {
  const steps = [];
  let K = 1;
  while (K < max) {
    steps.push(K);
    K = K < 10 ? K + 1 : roundTwoDigits(K * 1.2);
  }
  steps.push(Infinity);
  return steps;
}

function roundTwoDigits(value) {
  const unit = Math.pow(10, Math.floor(Math.log10(value)) - 1);
  return Math.round(value / unit) * unit;
}
